import React, { useMemo } from 'react';
import ComboSelector, { TipoClave } from '../../UI/campos/ComboSelector.js';
import CampoEditableFechaCampos from '../../UI/campos/CampoEditableFechaCampos.js';
import CampoEditableTexto from '../../UI/campos/CampoEditableTexto.js';
import MensajeGrabando from '../../UI/MensajeGrabando.js';
import * as Campos from '../../constantes/Campos.js';
import * as Fecha from '../../funciones/FuncionesFecha.js';
import { NombreEquipoTorneo } from '../../funciones/FuncionesEquipo.js';
import styles from './FilaPartidoFixture.module.css';

/**
 * Fila editable para un partido del fixture
 * Muestra campos: ordenPartido (editable), fecha, equipos, botones
 * Los partidos se muestran agrupados por zona, no hay columna zona
 */
const FilaPartidoFixture = (props) => {
  const partido = props.partido;
  const torneo = props.torneo; // Torneo para formatear nombres de equipos
  const esVuelta = props.esVuelta || false; // Si es true, los equipos no son editables
  const onChange = props.onChange; // (partido) => void
  const onGuardar = props.onGuardar; // () => void
  const onCancelar = props.onCancelar; // () => void
  const isGrabando = props.isGrabando || false; // Estado de grabando individual

  // Memoizar equipos para evitar crear array nuevo en cada render
  const equipos = useMemo(() => props.equipos || [], [props.equipos]);

  // Transformar lista de equipos para mostrar nombres con país si es torneo internacional
  const equiposConNombres = useMemo(() => {
    return equipos.map((equipo) => ({
      ...equipo,
      nombre: torneo ? NombreEquipoTorneo(equipo, torneo) : equipo.nombre,
    }));
  }, [equipos, torneo]);

  const onFechaChange = (campo, valor) => {
    onChange({
      ...partido,
      anioPartido: valor[Fecha.ANIO],
      mesPartido: valor[Fecha.MES],
      diaPartido: valor[Fecha.DIA],
    });
  };

  const onOrdenPartidoChange = (campo, valor) => {
    onChange({
      ...partido,
      ordenPartido: valor ? parseInt(valor, 10) : null,
    });
  };

  const onEquipoLocalChange = (equipoSeleccionado) => {
    // Buscar el equipo original en la lista (sin nombre transformado)
    const equipoOriginal =
      equipos.find((e) => e.id === equipoSeleccionado.id) || equipoSeleccionado;
    onChange({
      ...partido,
      equipoLocal: equipoOriginal,
    });
  };

  const onEquipoVisitanteChange = (equipoSeleccionado) => {
    // Buscar el equipo original en la lista (sin nombre transformado)
    const equipoOriginal =
      equipos.find((e) => e.id === equipoSeleccionado.id) || equipoSeleccionado;
    onChange({
      ...partido,
      equipoVisitante: equipoOriginal,
    });
  };

  // Validar que el partido tenga datos suficientes para guardar
  const puedeGuardar = partido.anioPartido && partido.equipoLocal && partido.equipoVisitante;

  // Mostrar resultado si existe
  const resultado =
    partido.resultadoLocal && partido.resultadoVisitante
      ? `${partido.resultadoLocal} - ${partido.resultadoVisitante}`
      : '-';

  // Obtener nombres de equipos con formato correcto
  const nombreLocal =
    torneo && partido.equipoLocal
      ? NombreEquipoTorneo(partido.equipoLocal, torneo)
      : partido.equipoLocal?.nombre || '';

  const nombreVisitante =
    torneo && partido.equipoVisitante
      ? NombreEquipoTorneo(partido.equipoVisitante, torneo)
      : partido.equipoVisitante?.nombre || '';

  return (
    <React.Fragment>
      <tr className={styles.fila}>
        <td className={styles.ordenPartido}>
          <CampoEditableTexto
            nombre={Campos.Partido.ORDEN_PARTIDO}
            valor={partido.ordenPartido ? partido.ordenPartido.toString() : ''}
            editar={true}
            largo={3}
            width="30px"
            onChange={onOrdenPartidoChange}
          />
        </td>
        <td className={styles.fecha}>
          <CampoEditableFechaCampos
            nombre="fecha"
            anio={partido.anioPartido}
            mes={partido.mesPartido}
            dia={partido.diaPartido}
            editar={true}
            onChange={onFechaChange}
          />
        </td>
        <td className={styles.equipoLocal}>
          {esVuelta ? (
            <span>{nombreLocal}</span>
          ) : (
            <ComboSelector
              tipoClave={TipoClave.ID}
              lista={equiposConNombres}
              campoClave="id"
              campoDescripcion="nombre"
              valorActual={nombreLocal}
              editar={true}
              onItemElegido={onEquipoLocalChange}
              placeholder="Seleccionar equipo local"
            />
          )}
        </td>
        <td className={styles.resultado}>{resultado}</td>
        <td className={styles.equipoVisitante}>
          {esVuelta ? (
            <span>{nombreVisitante}</span>
          ) : (
            <ComboSelector
              tipoClave={TipoClave.ID}
              lista={equiposConNombres}
              campoClave="id"
              campoDescripcion="nombre"
              valorActual={nombreVisitante}
              editar={true}
              onItemElegido={onEquipoVisitanteChange}
              placeholder="Seleccionar equipo visitante"
            />
          )}
        </td>
        <td className={styles.botones}>
          <button
            className={styles.btnGuardar}
            onClick={onGuardar}
            disabled={!puedeGuardar || isGrabando}
            title="Guardar partido"
          >
            ✓
          </button>
          <button
            className={styles.btnCancelar}
            onClick={onCancelar}
            disabled={isGrabando}
            title="Cancelar"
          >
            ✗
          </button>
        </td>
      </tr>
      {isGrabando && (
        <tr>
          <td colSpan="6" className={styles.filaGrabando}>
            <MensajeGrabando isGrabando={true} entidad="partido" />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default FilaPartidoFixture;
